import { useEffect, useState } from "react";
import { Form, Input, DatePicker, Select, Radio, Button, Checkbox } from "antd";
import { GetAllCountryApi } from "../../../../api/request/common.js";
import { getTaxDomicilesReasons } from "../../../../api/request/corporate.js";
import Loader from "../../../../components/Loader.jsx";
import {
  AlternativeMobileNumberValidator,
  mobileNumberValidator,
} from "../../../../utils/utils.js";
import {
  addUpdateAccountInformation,
  getPersonalInfo,
} from "../../../../api/request/individualOnboarding.js";
import { GetTraderByIdApi } from "../../../../api/request/client.js";
import LoadableButton from "../../../../components/buttons/LoadableButton.jsx";
import { handleApiError } from "../../../../utils/handleApiError.js";
import toast from "react-hot-toast";
import dayjs from "dayjs";

const { TextArea } = Input;

const AccountInformation = ({
  onNext,
  onPrevious,
  userId,
  isSubmitted,
  isReadOnly = false,
  canUpdate = true,
  id,
}) => {
  const [form] = Form.useForm();
  const [countries, setCountries] = useState([]);
  const [countryCodes, setCountryCodes] = useState([]);
  const [countryListRaw, setCountryListRaw] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isOtherNational, setIsOtherNational] = useState(false);
  const [isUSPerson, setIsUSPerson] = useState(null);
  const [isPEP, setIsPEP] = useState(null);
  const [accountInfoId, setAccountInfoId] = useState(null);
  const isDisabled = isSubmitted;
  const isOtherNationalValue = Form.useWatch("isAnotherNationality", form);
  const isUSPersonValue = Form.useWatch("areYouUSPerson", form);
  const isMultiTaxDomicile = Form.useWatch("isMultiTaxDomicileEnabled", form);
  const isPEPValue = Form.useWatch("isPoliticallyExposedPerson", form);

  const handleUSPersonChange = (e) => {
    const value = e.target.value;
    setIsUSPerson(value);
    if (!value) {
      form.setFieldsValue({ acceptDeclaration: false });
    }
  };

  const handlePEPChange = (e) => {
    const value = e.target.value;
    setIsPEP(value);
    if (!value) {
      form.setFieldsValue({ pepDetails: "" });
    }
  };

  const getAllCountries = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetAllCountryApi();
      if (data?.success) {
        const countryData = data?.data || [];
        setCountryListRaw(countryData);
        setCountries(
          countryData?.map((item) => ({
            value: item?.id,
            label: item?.countryName,
          }))
        );

        setCountryCodes(
          countryData
            ?.map((item) => {
              const dialCode = item?.countryCode1 || item?.dialCode || item?.countryCode;
              return {
                value: item?.id ?? dialCode,
                label: dialCode,
                dialCode,
                countryShortCode: item?.countryCode2 || "",
              };
            })
            ?.filter((item) => item?.value)
        );
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAllReasons = async () => {
    try {
      const { data } = await getTaxDomicilesReasons();
      if (data?.success) {
        setReasons(
          data?.data?.map((item) => ({ value: item?.id, label: item?.name }))
        );
      }
    } catch (error) {
      console.error("Error fetching tax domicile reasons:", error);
    }
  };

  const loadPersonInfo = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const { data } = await getPersonalInfo(id, userId);
      if (data?.success && data?.data) {
        const personalInfo = data.data;

        const parseStoredPhone = (value) => {
          const trimmed = String(value || "").trim();
          if (!trimmed) return { code: undefined, number: "" };

          const [dialCode, ...numberParts] = trimmed.split(" ");
          const matchedCode = countryCodes.find(
            (item) => item.dialCode === dialCode || item.label === dialCode
          );

          return {
            code: matchedCode?.value,
            number: numberParts.join(" ").trim(),
          };
        };

        const mobilePhone = parseStoredPhone(personalInfo.phoneNumber);
        const alternatePhone = parseStoredPhone(personalInfo.alternatePhoneNumber);
        const dob = personalInfo.dob ? dayjs(personalInfo.dob) : null;

        form.setFieldsValue({
          acceptDeclaration: true,
          title: personalInfo.title || undefined,
          firstName: personalInfo.firstName || "",
          lastName: personalInfo.lastName || "",
          dob: dob,
          placeOfBirth: personalInfo.placeOfBirth || "",
          gender: personalInfo.gender || "",
          nationalityId: personalInfo.nationalityId || undefined,
          isAnotherNationality: personalInfo.isAnotherNationality || false,
          multiNationalityIds: personalInfo.multiNationalityIds || [],
          address: personalInfo.address || "",
          addressLine2: personalInfo.addressLine2 || "",
          apartmentVillaInfo: personalInfo.apartmentVillaInfo || "",
          streetAreaInfo: personalInfo.streetAreaInfo || "",
          residenceCountryId: personalInfo.residenceCountryId || undefined,
          cityTown: personalInfo.cityTown || "",
          mobileCountryCode: mobilePhone.code,
          mobileNumber: mobilePhone.number,
          alternateCountryCode: alternatePhone.code,
          alternatePhoneNumber: alternatePhone.number,
          postalZipCode: personalInfo.postalZipCode || "",
          areYouUSPerson: personalInfo.areYouUSPerson || false,
          isMultiTaxDomicileEnabled:
            personalInfo.isMultiTaxDomicileEnabled || false,
          taxDomiciles:
            personalInfo.taxDomiciles && personalInfo.taxDomiciles.length > 0
              ? personalInfo.taxDomiciles.map((td) => ({
                id: td.id,
                taxDomicileCountryId: td.taxDomicileCountryId || null,
                taxIDNumber: td.taxIDNumber || "",
                isTINIDAvailable: td.isTINIDAvailable || false,
                taxDomicileReasonID: td.taxDomicileReasonID || null,
                taxDomicileRegEx: td.taxDomicileRegEx || "",
                alternateTaxIdentifier: td.alternateTaxIdentifier || "",
              }))
              : [
                {
                  taxDomicileCountryId: null,
                  taxIDNumber: "",
                  isTINIDAvailable: false,
                  taxDomicileReasonID: null,
                  alternateTaxIdentifier: "",
                },
              ],
          isPoliticallyExposedPerson:
            personalInfo.isPoliticallyExposedPerson || false,
          pepDetails: personalInfo.pepDetails || "",
        });

        setAccountInfoId(personalInfo.id);
      }
    } catch (error) {
      console.error("Error loading personal info:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserDetailsForAutofill = async () => {
    if (!userId || id || countryListRaw.length === 0) return;
    try {
      const { data } = await GetTraderByIdApi(userId);
      if (!data?.success || !data?.data) return;
      const user = data.data;
      const countryByName = countryListRaw.find(
        (c) =>
          (c?.countryName || "").toLowerCase() ===
          (user?.countryName || "").toLowerCase()
      );
      form.setFieldsValue({
        firstName: user?.firstName ?? "",
        lastName: user?.lastName ?? "",
        residenceCountryId: countryByName?.id ?? undefined,
      });
    } catch (error) {
      console.error("Error loading user details for autofill:", error);
    }
  };

  useEffect(() => {
    setIsOtherNational(isOtherNationalValue === true);
  }, [isOtherNationalValue]);

  useEffect(() => {
    setIsUSPerson(isUSPersonValue);
  }, [isUSPersonValue]);

  useEffect(() => {
    setIsPEP(isPEPValue);
  }, [isPEPValue]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        await Promise.all([getAllCountries(), getAllReasons()]);
      } catch (error) {
        console.error("Error initializing:", error);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (id && countries.length > 0) {
      loadPersonInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, userId, countries.length]);

  useEffect(() => {
    if (!id && userId && countryListRaw.length > 0) {
      loadUserDetailsForAutofill();
    }
  }, [id, userId, countryListRaw.length]);

  const handleMultiTaxResidentChange = (e) => {
    const isMultiTaxDomicile = e?.target?.value;
    const taxDomiciles = form.getFieldValue("taxDomiciles") || [];

    if (isMultiTaxDomicile) {
      if (!taxDomiciles?.length) {
        form.setFieldsValue({
          taxDomiciles: [
            {
              taxDomicileCountryId: null,
              taxIDNumber: "",
              isTINIDAvailable: false,
              taxDomicileReasonID: null,
              alternateTaxIdentifier: "",
            },
          ],
        });
      }
    } else {
      if (taxDomiciles?.length > 1) {
        form.setFieldsValue({
          taxDomiciles: [taxDomiciles[0]],
        });
      }
    }
  };

  const filterOption = (input, option) => {
    return (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
  };

  const filterCountryCodeOption = (input, option) => {
    const search = input.toLowerCase().replace(/^\+/, "");
    const dialCode = String(option?.dialCode ?? option?.label ?? "")
      .toLowerCase()
      .replace(/^\+/, "");
    const shortCode = String(option?.countryShortCode ?? "").toLowerCase();
    return dialCode.includes(search) || shortCode.includes(search);
  };

  const resolveDialCode = (selected) =>
    countryCodes.find((item) => item.value === selected)?.dialCode || selected;

  const handleSubmit = async (values) => {
    if (isReadOnly) return;
    try {
      setIsSubmitLoading(true);

      const dob =
        values.dob && dayjs(values.dob).isValid()
          ? dayjs(values.dob).toISOString()
          : null;

      const phoneNumber =
        values.mobileCountryCode && values.mobileNumber
          ? `${resolveDialCode(values.mobileCountryCode)} ${values.mobileNumber}`.trim()
          : "";

      const alternatePhoneNumber =
        values.alternateCountryCode && values.alternatePhoneNumber
          ? `${resolveDialCode(values.alternateCountryCode)} ${values.alternatePhoneNumber}`.trim()
          : "";

      const taxDomiciles = (values.taxDomiciles || []).map((item) => {
        const taxDomicile = {
          taxDomicileCountryId: item.taxDomicileCountryId || 0,
          taxIDNumber: item.taxIDNumber || "",
          isTINIDAvailable: item.isTINIDAvailable || false,
          taxDomicileReasonID: item.taxDomicileReasonID || 0,
          taxDomicileRegEx: item.taxDomicileRegEx || "",
          alternateTaxIdentifier: item.alternateTaxIdentifier || "",
        };
        if (item.id) {
          taxDomicile.id = item.id;
        }
        return taxDomicile;
      });

      const payload = {
        userId: userId || "",
        firstName: values.firstName || "",
        lastName: values.lastName || "",
        dob: dob,
        placeOfBirth: values.placeOfBirth || "",
        gender: values.gender || "",
        nationalityId: values.nationalityId || 0,
        isAnotherNationality: values.isAnotherNationality || false,
        multiNationalityIds: values.multiNationalityIds || [],
        address: values.address || "",
        addressLine2: values.addressLine2 || "",
        apartmentVillaInfo: values.apartmentVillaInfo || "",
        streetAreaInfo: values.streetAreaInfo || "",
        residenceCountryId: values.residenceCountryId || 0,
        cityTown: values.cityTown || "",
        phoneNumber: phoneNumber,
        alternatePhoneNumber: alternatePhoneNumber,
        postalZipCode: values.postalZipCode || "",
        areYouUSPerson: values.areYouUSPerson || false,
        taxDomiciles: taxDomiciles,
        isPoliticallyExposedPerson: values.isPoliticallyExposedPerson || false,
        pepDetails: values.pepDetails || "",
      };

      if (values.title) {
        payload.title = values.title;
      }

      if (values.refferedById) {
        payload.refferedById = values.refferedById;
      }

      if (id) {
        payload.id = id;
      } else if (accountInfoId) {
        payload.id = accountInfoId;
      }

      const { data } = await addUpdateAccountInformation(payload);

      if (data?.success) {
        toast.success(
          data?.message || "Account information saved successfully"
        );
        if (data?.data?.id) {
          setAccountInfoId(data.data.id);
        }
        onNext();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <Form
        form={form}
        size="large"
        layout="vertical"
        autoComplete="off"
        className={isReadOnly ? "corporate-review-readonly" : undefined}
        disabled={isDisabled && !isReadOnly}
        onKeyDown={(event) => {
          if (!isReadOnly) return;
          if (event.target.closest?.(".corporate-review-nav")) return;
          event.preventDefault();
        }}
        onFinish={handleSubmit}
        initialValues={{
          isAnotherNationality: false,
          isMultiTaxDomicileEnabled: false,
          taxDomiciles: [
            {
              taxDomicileCountryId: null,
              taxIDNumber: "",
              isTINIDAvailable: false,
              taxDomicileReasonID: null,
              alternateTaxIdentifier: "",
            },
          ],
        }}
      >
        <div className="border border-gray-300 rounded-lg p-6">
          <h2 className="text-2xl font-semibold mb-6">Personal information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <Form.Item
                label={<span>First Name/Given Name</span>}
                name="firstName"
                rules={[{ required: true, message: "Please enter first name" }]}
              >
                <Input placeholder="Enter first name" disabled />
              </Form.Item>

              <Form.Item
                label={<span>Date Of Birth</span>}
                name="dob"
                rules={[
                  { required: true, message: "Please select date of birth" },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const latestAllowedDob = dayjs()
                        .subtract(18, "year")
                        .endOf("day");
                      if (dayjs(value).isAfter(latestAllowedDob)) {
                        return Promise.reject(
                          new Error("User must be at least 18 years old"),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  placeholder="DD/MM/YYYY"
                  className="w-full"
                  style={{ width: "100%" }}
                  defaultPickerValue={dayjs().subtract(18, "year")}
                  disabledDate={(current) =>
                    current &&
                    current >
                      dayjs().subtract(18, "year").endOf("day")
                  }
                />
              </Form.Item>

              <Form.Item
                label={<span>Place Of Birth</span>}
                name="placeOfBirth"
                rules={[
                  { required: true, message: "Please enter place of birth" },
                ]}
              >
                <Input placeholder="Enter place of birth" />
              </Form.Item>

              <Form.Item
                label="Are you a national of any other country?"
                name="isAnotherNationality"
                rules={[{ required: true, message: "Please select an option" }]}
              >
                <Radio.Group>
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>

            </div>

            <div className="space-y-4">
              <Form.Item
                label={<span>Last Name/Surname</span>}
                name="lastName"
                rules={[{ required: true, message: "Please enter last name" }]}
              >
                <Input placeholder="Enter last name" disabled />
              </Form.Item>

              <Form.Item
                label={<span>Gender</span>}
                name="gender"
                rules={[{ required: true, message: "Please select gender" }]}
              >
                <Radio.Group>
                  <Radio value="Male">Male</Radio>
                  <Radio value="Female">Female</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                label={<span>Nationality 1</span>}
                name="nationalityId"
                rules={[
                  { required: true, message: "Please select nationality" },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select Country"
                  filterOption={filterOption}
                  options={countries}
                />
              </Form.Item>
            </div>

            {isOtherNational && (
              <Form.Item
                label="Select Other Nationalities"
                name="multiNationalityIds"
                rules={[
                  {
                    required: true,
                    message: "Please select at least one country",
                  },
                ]}
                className="md:col-span-2 mb-0"
              >
                <Select
                  mode="multiple"
                  showSearch
                  maxTagCount="responsive"
                  placeholder="Select countries"
                  filterOption={filterOption}
                  options={countries}
                  className="w-full [&_.ant-select-selector]:!h-auto [&_.ant-select-selector]:!min-h-9 [&_.ant-select-selector]:!py-1 [&_.ant-select-selector]:items-center [&_.ant-select-selection-overflow]:flex-nowrap [&_.ant-select-selection-overflow]:items-center"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            )}
          </div>
        </div>

        <div className="border border-gray-300 rounded-lg p-6 mt-6">
          <h2 className="text-2xl font-semibold mb-2">
            Current Residential Address
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            (As it appears on your proof of residence document)
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              label={
                <span>
                  Current Residential address (As it appears on your proof of
                  residence document){" "}
                </span>
              }
              name="address"
              rules={[
                {
                  required: true,
                  message: "Please enter current residential address",
                },
              ]}
              className="md:col-span-2"
            >
              <Input placeholder="Enter current residential address" />
            </Form.Item>

            <Form.Item
              label="Address Line 2"
              name="addressLine2"
              className="md:col-span-2"
            >
              <Input placeholder="Enter address line 2" />
            </Form.Item>

            <Form.Item
              label={<span>Building and Apartment Number </span>}
              name="apartmentVillaInfo"
              rules={[
                {
                  required: true,
                  message: "Please enter building and apartment number",
                },
              ]}
            >
              <Input placeholder="Enter building and apartment number" />
            </Form.Item>

            <Form.Item
              label={<span>Street Name</span>}
              name="streetAreaInfo"
              rules={[{ required: true, message: "Please enter street name" }]}
            >
              <Input placeholder="Enter street name" />
            </Form.Item>

            <Form.Item
              label={<span>Country</span>}
              name="residenceCountryId"
              rules={[{ required: true, message: "Please select country" }]}
            >
              <Select
                showSearch
                placeholder="Select Country"
                filterOption={filterOption}
                options={countries}
                disabled
              />
            </Form.Item>

            <Form.Item
              label={<span>City</span>}
              name="cityTown"
              rules={[{ required: true, message: "Please enter city" }]}
            >
              <Input placeholder="Enter city" />
            </Form.Item>

            <Form.Item label={<span>Mobile Number</span>} required>
              <div className="flex w-full gap-2">
                <Form.Item
                  name="mobileCountryCode"
                  noStyle
                  rules={[{ required: true, message: "Country code required" }]}
                >
                  <Select
                    showSearch
                    placeholder="Code"
                    filterOption={filterCountryCodeOption}
                    options={countryCodes}
                    style={{ width: "30%" }}
                  />
                </Form.Item>
                <Form.Item
                  name="mobileNumber"
                  noStyle
                  dependencies={["mobileCountryCode"]}
                  rules={[mobileNumberValidator()]}
                >
                  <Input
                    placeholder="Enter mobile number"
                    maxLength={15}
                  />
                </Form.Item>
              </div>
            </Form.Item>

            <Form.Item label="Alternative Phone Number">
              <div className="flex w-full gap-2">
                <Form.Item name="alternateCountryCode" noStyle>
                  <Select
                    showSearch
                    placeholder="Code"
                    filterOption={filterCountryCodeOption}
                    options={countryCodes}
                    style={{ width: "30%" }}
                  />
                </Form.Item>
                <Form.Item
                  name="alternatePhoneNumber"
                  noStyle
                  dependencies={["alternateCountryCode"]}
                  rules={[AlternativeMobileNumberValidator()]}
                >
                  <Input
                    placeholder="e.g. +91 81234 56789"
                    maxLength={15}
                    inputMode="numeric"
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              </div>
            </Form.Item>

            <Form.Item
              label="PO Box"
              name="postalZipCode"
              className="md:col-span-2"
            >
              <Input placeholder="Enter PO Box" />
            </Form.Item>
          </div>
        </div>

        <div className="border border-gray-300 rounded-lg p-6 mt-6">
          <h2 className="text-2xl font-semibold mb-2">FATCA and CRS</h2>
          <p className="text-sm text-gray-600 mb-4">
            In accordance with the Foreign Account Tax Compliance Act, 2010
            (FATCA), please answer the following questions:
          </p>

          <Form.Item
            label={
              <span className="font-semibold">Are you a U.S. Person?</span>
            }
            name="areYouUSPerson"
            rules={[{ required: true, message: "Please select an option" }]}
          >
            <Radio.Group onChange={handleUSPersonChange}>
              <Radio value={true}>Yes</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
          </Form.Item>

          {isUSPerson !== null && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-4">
                Please note this also includes if you are a US green Card
                holder, hold a US bank account, have a US PO box or postal
                address, have a US based power of attorney etc. Please visit the
                website www.irs.gov for full details or consult a foreign tax
                advisor.
              </p>

              {/* Declaration Box */}
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 mb-4">
                <p className="text-sm text-gray-700 mb-3">
                  {isUSPerson
                    ? "I declare that I AM a U.S. Person and am subject to U.S. federal income taxes. I, hereby represent that, and by checking the applicable box below, the information provided by me is true and accurate."
                    : "I declare that I AM not a U.S. Person and am subject to U.S. federal income taxes. I, hereby represent that, and by checking the applicable box below, the information provided by me is true and accurate."}
                </p>
                <Form.Item
                  name="acceptDeclaration"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(
                            new Error("Please accept the declaration")
                          ),
                    },
                  ]}
                >
                  <Checkbox>I accept above declaration</Checkbox>
                </Form.Item>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6">
          <p className="text-sm text-gray-600 !font-normal mb-4">
            For Common Reporting Standard (CRS) purposes, please mention which
            countries you are tax resident of?{" "}
          </p>
          <div className="border border-gray-300 rounded-lg p-6 mt-6">
            <Form.List name="taxDomiciles">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <div
                      key={field.key}
                      className="border border-gray-300 rounded-lg p-6 mt-6"
                    >
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">
                          {index === 0
                            ? "Tax Resident Information"
                            : `Additional Tax Resident Information ${index}`}
                        </h3>

                        {index !== 0 && (
                          <Button
                            type="link"
                            danger
                            onClick={() => remove(field.name)}
                            disabled={isDisabled}
                          >
                            Remove
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                          label={<span>Select Country</span>}
                          name={[field.name, "taxDomicileCountryId"]}
                          rules={[
                            {
                              required: true,
                              message: "Please select country",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            placeholder="Select country"
                            filterOption={filterOption}
                            options={countries}
                            disabled={isDisabled}
                          />
                        </Form.Item>

                        <Form.Item shouldUpdate noStyle>
                          {({ getFieldValue }) => {
                            const noTIN = getFieldValue([
                              "taxDomiciles",
                              index,
                              "isTINIDAvailable",
                            ]);

                            return (
                              <Form.Item
                                label="Tax Identification Number"
                                name={[field.name, "taxIDNumber"]}
                                rules={
                                  noTIN
                                    ? []
                                    : [
                                      {
                                        required: true,
                                        message: "Enter TIN",
                                      },
                                    ]
                                }
                                className="w-full"
                                disabled={noTIN || isDisabled}
                              >
                                <Input
                                  placeholder="Enter tax identification number"
                                  disabled={noTIN || isDisabled}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>

                        <Form.Item
                          name={[field.name, "isTINIDAvailable"]}
                          valuePropName="checked"
                          className="md:col-span-2"
                        >
                          <Checkbox disabled={isDisabled}>
                            Check if you don't have TIN?
                          </Checkbox>
                        </Form.Item>

                        <Form.Item shouldUpdate noStyle>
                          {({ getFieldValue }) => {
                            const isTIN = !getFieldValue([
                              "taxDomiciles",
                              index,
                              "isTINIDAvailable",
                            ]);

                            if (isTIN) return null;

                            return (
                              <Form.Item
                                label="Please select a reason"
                                name={[field.name, "taxDomicileReasonID"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Please select reason",
                                  },
                                ]}
                                className="md:col-span-2"
                              >
                                <Radio.Group disabled={isDisabled}>
                                  {reasons.map((r) => (
                                    <Radio
                                      key={r.value}
                                      value={r.value}
                                      className="block mb-2"
                                    >
                                      {r.label}
                                    </Radio>
                                  ))}
                                </Radio.Group>
                              </Form.Item>
                            );
                          }}
                        </Form.Item>

                        <Form.Item shouldUpdate noStyle>
                          {({ getFieldValue }) => {
                            const tinUnavailable = getFieldValue([
                              "taxDomiciles",
                              index,
                              "isTINIDAvailable",
                            ]);

                            if (!tinUnavailable) return null;

                            return (
                              <Form.Item
                                label="ID or Passport Number"
                                name={[field.name, "alternateTaxIdentifier"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Please enter alternate tax identifier",
                                  },
                                ]}
                                className="md:col-span-2"
                              >
                                <Input
                                  placeholder="ID or Passport Number"
                                  disabled={isDisabled}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </div>
                    </div>
                  ))}

                  {isMultiTaxDomicile && (
                    <div className="mt-6">
                      <Button
                        type="dashed"
                        block
                        onClick={() =>
                          add({
                            taxDomicileCountryId: null,
                            taxIDNumber: "",
                            isTINIDAvailable: false,
                            taxDomicileReasonID: null,
                            alternateTaxIdentifier: "",
                          })
                        }
                        disabled={isDisabled}
                      >
                        + Add Another Tax Resident Country
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Form.List>
            <div className="mt-6">
              <Form.Item
                label="Are there any other countries you are tax resident of?"
                name="isMultiTaxDomicileEnabled"
                rules={[{ required: true, message: "Please select an option" }]}
              >
                <Radio.Group onChange={handleMultiTaxResidentChange}>
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          </div>
        </div>

        <div className="border border-gray-300 rounded-lg p-6 mt-6">
          <h3 className="text-2xl font-semibold mb-4 ">
            Politically Exposed Person?
          </h3>

          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              PEP shall include Heads of States or Governments, senior
              politicians, senior government officials, judicial or military
              officials, senior executive managers of state owned corporations,
              and senior officials of political parties and persons who are, or
              have previously been, entrusted with the management of an
              international organisation or any prominent function within such
              an organisation; and the definition also includes the following:
            </p>

            <div className="ml-4 mb-2">
              <p className="text-sm text-gray-600 mb-1">
                <strong>1. Direct family members</strong> (Of the PEP, who are
                spouses, children, spouses of children, parents).
              </p>
              <p className="text-sm text-gray-600">
                <strong>
                  2. Associates known to be close to the PEP, which include:
                </strong>
              </p>
              <ul className="list-disc ml-6 mt-1 text-sm text-gray-600">
                <li>
                  Individuals having joint ownership rights in a legal person or
                  arrangement or any other close business relationship with the
                  PEP.
                </li>
                <li>
                  Individuals having individual ownership rights in a legal
                  person or arrangement established in favour of the PEP.
                </li>
              </ul>
            </div>
          </div>

          <Form.Item
            name="isPoliticallyExposedPerson"
            rules={[{ required: true, message: "Please select an option" }]}
          >
            <Radio.Group onChange={handlePEPChange} disabled={isDisabled}>
              <Radio value={true}>Yes</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
          </Form.Item>

          {isPEP && (
            <Form.Item
              label={<span>Please provide details</span>}
              name="pepDetails"
              rules={[
                { required: true, message: "Please provide PEP details" },
              ]}
              className="mt-4"
            >
              <TextArea
                rows={4}
                placeholder="Please provide details about being a Politically Exposed Person"
                disabled={isDisabled}
              />
            </Form.Item>
          )}
        </div>

        <div className="flex justify-end gap-4 mt-6">
          {/* {onPrevious && (
            <Button onClick={onPrevious} size="large" disabled={isDisabled}>
              Previous
            </Button>
          )} */}
          {canUpdate && (
            <LoadableButton
              type="submit"
              lable="Next"
              isLoading={isSubmitLoading}
              loadingLable="Saving..."
              disabled={isDisabled || isSubmitLoading}
              className="bg-primary text-white text-base font-semibold rounded-lg px-12 py-2 themeHover duration-500"
            />
          )}
        </div>
      </Form>
      {!canUpdate && (
        <div className="flex justify-end gap-4 mt-6">
          <Button type="primary" size="large" onClick={onNext}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default AccountInformation;
